// Client-side mirror of backend/auth_policy.py validate_password; the server stays the authority.

export const MIN_LENGTH = 8;
export const MAX_LENGTH = 256;
export const MIN_CLASSES = 3;
export const MAX_RUN = 3;

export interface PasswordIdentity {
  FirstName?: string | null;
  LastName?: string | null;
  EMail?: string | null;
}

export interface PasswordRule {
  label: string;
  met: boolean | null;
}

const UPPER = /\p{Uppercase}/u;
const LOWER = /\p{Lowercase}/u;
const DIGIT = /\p{Nd}/u;
const LETTER_OR_NUMBER = /[\p{L}\p{N}]/u;
const ASCII_LETTER = /^[A-Za-z]$/;

function codePoints(value: string): string[] {
  return Array.from(value);
}

export function longestRepeat(value: string): number {
  let longest = 0;
  let run = 0;
  let prev: string | null = null;
  for (const ch of codePoints(value.toLowerCase())) {
    run = ch === prev ? run + 1 : 1;
    prev = ch;
    longest = Math.max(longest, run);
  }
  return longest;
}

export function longestSequence(value: string): number {
  const chars = codePoints(value.toLowerCase());
  let longest = value ? 1 : 0;
  let up = longest;
  let down = longest;
  for (let i = 1; i < chars.length; i++) {
    const prev = chars[i - 1];
    const ch = chars[i];
    const sameClass = (DIGIT.test(prev) && DIGIT.test(ch)) || (ASCII_LETTER.test(prev) && ASCII_LETTER.test(ch));
    const step = sameClass ? ch.codePointAt(0) - prev.codePointAt(0) : 0;
    up = step === 1 ? up + 1 : 1;
    down = step === -1 ? down + 1 : 1;
    longest = Math.max(longest, up, down);
  }
  return longest;
}

export function characterClasses(value: string): number {
  const chars = codePoints(value);
  return [
    chars.some(c => UPPER.test(c)),
    chars.some(c => LOWER.test(c)),
    chars.some(c => DIGIT.test(c)),
    chars.some(c => !LETTER_OR_NUMBER.test(c)),
  ].filter(Boolean).length;
}

export function personalIdentifiers(user: PasswordIdentity): string[] {
  const email = user.EMail || '';
  return [email.split('@')[0], user.FirstName || '', user.LastName || '']
    .map(p => p.trim())
    .filter(p => codePoints(p).length >= 3)
    .map(p => p.toLowerCase());
}

export function containsPersonalInfo(value: string, user: PasswordIdentity | null): boolean {
  if (!user) {
    return false;
  }
  const lowered = value.toLowerCase();
  return personalIdentifiers(user).some(part => lowered.includes(part));
}

/** Checklist state for the given inputs; met is null until either field has been typed in. */
export function passwordRules(password: string, confirm: string, user: PasswordIdentity | null,
                              checkPersonal: boolean): PasswordRule[] {
  password = password || '';
  confirm = confirm || '';
  const typed = password !== '' || confirm !== '';
  const length = codePoints(password).length;
  const rules: [string, () => boolean][] = [
    ['At least 8 characters (and at most 256)', () => length >= MIN_LENGTH && length <= MAX_LENGTH],
    ['At least 3 of: an uppercase letter, a lowercase letter, a number, a special character',
      () => characterClasses(password) >= MIN_CLASSES],
    ['No character repeated more than 3 times in a row', () => longestRepeat(password) <= MAX_RUN],
    ['No more than 3 sequential letters or numbers, such as abcd or 4321', () => longestSequence(password) <= MAX_RUN],
  ];
  if (checkPersonal) {
    rules.push(['Does not contain your first name, last name or the part of your email before the @', () => !containsPersonalInfo(password, user)]);
  }
  rules.push(['New Password and Confirm Password match', () => password === confirm]);
  return rules.map(([label, check]) => ({ label, met: typed ? check() : null }));
}
